
import { Card, CardContent } from '@/components/ui/card';
import { Payslip, Employee } from '@/types';
import { PayslipHeader } from './PayslipHeader';
import { PayslipCouncilHeader } from './PayslipCouncilHeader';
import { EmployeeInformation } from './EmployeeInformation';
import { SalaryBreakdown } from './SalaryBreakdown';
import { DeductionsBreakdown } from './DeductionsBreakdown';
import { NetPayDisplay } from './NetPayDisplay';
import { PayslipSummary } from './PayslipSummary';
import { PayslipFooter } from './PayslipFooter';
import { PayslipDataNotice } from './PayslipDataNotice';

interface PayslipDetailsProps {
  payslip: Payslip;
  employee?: Employee;
  employeeName?: string;
  onDownload?: () => void;
  onPrint?: () => void;
  onEmail?: () => void;
}

export const PayslipDetails = ({ 
  payslip, 
  employee, 
  employeeName, 
  onDownload = () => {},
  onPrint = () => {},
  onEmail = () => {}
}: PayslipDetailsProps) => {
  // Use employeeName if provided, otherwise try to get it from employee object
  const displayName = employeeName || (employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown Employee');

  // Ensure allowanceBreakdown and deductionBreakdown exist with default empty arrays
  const allowances = payslip.allowanceBreakdown || [];
  const deductions = payslip.deductionBreakdown || [];
  const hasAllowances = allowances && allowances.length > 0;
  const hasDeductions = deductions && deductions.length > 0;

  return (
    <Card className="h-auto max-h-[calc(100vh-220px)] overflow-auto md:h-[calc(100vh-280px)]">
      <PayslipHeader onDownload={onDownload} onPrint={onPrint} onEmail={onEmail} />
      <CardContent className="px-3 sm:px-6">
        <div className="bg-white rounded-md">
          <PayslipCouncilHeader council={payslip.council} payPeriod={payslip.payPeriod} />
          
          <PayslipDataNotice 
            hasAllowances={hasAllowances}
            hasDeductions={hasDeductions}
          />
          
          <EmployeeInformation 
            employee={employee || payslip as any}
            displayName={displayName}
          />
          
          <SalaryBreakdown
            basicPay={payslip.basicPay}
            basicSalary={payslip.basicSalary}
            allowances={allowances}
            grossSalary={payslip.grossSalary}
          />
          
          <DeductionsBreakdown
            deductions={deductions}
            totalDeductions={payslip.totalDeductions}
          />

          <PayslipSummary
            grossPayYTD={payslip.grossPayYTD}
            taxMonthly={payslip.taxMonthly}
            taxYTD={payslip.taxYTD}
            napsaMonthly={payslip.napsaMonthly}
            lasfMonthly={payslip.lasfMonthly}
            napsaYTD={payslip.napsaYTD}
            lasfYTD={payslip.lasfYTD}
            pspfYTD={payslip.pspfYTD}
            openingLeaveDays={payslip.openingLeaveDays}
            closingLeaveDays={payslip.closingLeaveDays}
            closingLeaveDaysValue={payslip.closingLeaveDaysValue}
            advanceBF={payslip.advanceBF}
            advanceCF={payslip.advanceCF}
          />
          
          <NetPayDisplay netSalary={payslip.netSalary} />
          
          <PayslipFooter generatedDate={payslip.generatedDate} />
        </div>  
      </CardContent>
    </Card>
  );
};
